import { css } from '@emotion/react';
import {
  FocusEvent,
  HTMLAttributes,
  InputHTMLAttributes,
  KeyboardEvent,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { cx } from './helpers';

export type InputProps = {
  id: string;
  label: string;
  type: 'text' | 'password' | 'number' | 'email' | 'tel';
  floatLabel?: boolean;
  innerLeftComponent?: ReactElement;
  onFocus?: (e: FocusEvent) => void;
  onBlur?: (e: FocusEvent) => void;
  onValueChange: (v: string) => void;
  onKeyDown?: (e: KeyboardEvent) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  autoComplete?: HTMLInputElement['autocomplete'];
  value: string;
  subText?: string;
  subTextFlavor?: 'error' | 'success';
  innerRightComponent?: ReactElement;
  onInputRef?: (ref: HTMLInputElement | null) => void;
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  maxLength?: HTMLInputElement['maxLength'];
  pattern?: HTMLInputElement['pattern'];
  ariaProps?: Pick<
    InputHTMLAttributes<HTMLInputElement>,
    | 'role'
    | 'aria-expanded'
    | 'aria-controls'
    | 'aria-autocomplete'
    | 'aria-activedescendant'
  >;
};

export function Input(props: InputProps): JSX.Element {
  const {
    label,
    id,
    type,
    value,
    onValueChange,
    floatLabel = true,
    onFocus,
    onBlur,
    onKeyDown,
    disabled,
    autoFocus,
    autoComplete,
    innerRightComponent,
    subText,
    subTextFlavor,
    innerLeftComponent,
    onInputRef,
    inputMode,
    maxLength,
    pattern,
    ariaProps,
  } = props;
  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);
  const [autofill, setAutofill] = useState(false);
  const [left, setLeft] = useState(0);
  const [right, setRight] = useState(0);

  // The label and the padding make room for the inner components, so measure them.
  const measuredLeftRef = useCallback((node: HTMLDivElement | null) => {
    setLeft(node ? node.getBoundingClientRect().width + 8 : 0);
  }, []);
  const measuredRightRef = useCallback((node: HTMLDivElement | null) => {
    setRight(node ? node.getBoundingClientRect().width + 8 : 0);
  }, []);

  useEffect(() => {
    onInputRef?.(inputRef);
  }, [onInputRef, inputRef]);

  // Browsers fire no event on autofill, so index.css starts the `onautofillstart`/`onautofillcancel`
  // animations on `:-webkit-autofill` and the label floats when one of them starts.
  const onAnimationStart = useCallback((e: AnimationEvent) => {
    if (e.animationName === 'onautofillstart') {
      setAutofill(true);
    } else if (e.animationName === 'onautofillcancel') {
      setAutofill(false);
    }
  }, []);

  useEffect(() => {
    if (!inputRef) {
      return undefined;
    }
    inputRef.addEventListener('animationstart', onAnimationStart);
    return () => {
      inputRef.removeEventListener('animationstart', onAnimationStart);
    };
  }, [inputRef, onAnimationStart]);

  return (
    <div
      className={cx('aura--input-container', {
        'has-value': !!value.length,
        'has-error': subTextFlavor === 'error',
        'has-autofill': autofill,
        'no-float': floatLabel === false,
      })}
    >
      <label
        htmlFor={id}
        className="aura--input-label"
        css={css({ left: left || 18, right: right || 18 })}
      >
        {label}
      </label>
      <div className="aura--input-internal-container">
        {innerLeftComponent && (
          <div
            ref={measuredLeftRef}
            className="aura--input-inner-left-component-wrapper"
          >
            <div>{innerLeftComponent}</div>
          </div>
        )}
        <input
          id={id}
          ref={setInputRef}
          className="aura--input"
          css={css({
            padding: 0,
            margin: 0,
            paddingLeft: left || 18,
            paddingRight: right || 18,
            marginLeft: -left,
            marginRight: -right,
            width: `calc(100% + ${left}px + ${right}px)`,
          })}
          value={value}
          onChange={e => onValueChange(e.target.value)}
          type={type}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          inputMode={inputMode}
          maxLength={maxLength}
          pattern={pattern}
          {...ariaProps}
        />
        <fieldset
          className="aura--input-fieldset"
          css={css({
            paddingLeft: (left || 18) - 8,
            height: 'calc(100% + 8px)',
          })}
        >
          <legend className="aura--input-legend">{label}</legend>
        </fieldset>
        {innerRightComponent && (
          <div
            ref={measuredRightRef}
            className="aura--input-inner-right-component-wrapper"
          >
            <div>{innerRightComponent}</div>
          </div>
        )}
      </div>
      {subText && (
        <div
          className={cx('aura--input-subtext', {
            'aura--error-label': subTextFlavor === 'error',
            'aura--success-label': subTextFlavor === 'success',
          })}
        >
          {subText}
        </div>
      )}
    </div>
  );
}
